import * as React from "react";
import { useTranslation } from "react-i18next";

import styles from "./KnowledgeBaseView.scss";
import Cross from "components/icons/Cross";
import Search from "components/icons/Search";
import style from "components/licenses/license-configuration/bms/bms-config.scss";
import { KnowledgeBaseArticle, knowledgeBaseService, MAX_SEARCH_TERM_LENGTH } from "services/news/KnowledgeBaseService";
import { logger } from "utils/logging";

interface Props {
    selectedLanguage: string;
    onArticleClick: (article: KnowledgeBaseArticle) => void;
}

export const KnowledgeBaseSearch: React.FC<Props> = ({ selectedLanguage, onArticleClick }) => {
    const { t } = useTranslation();
    const [searchTerm, setSearchTerm] = React.useState<string>("");
    const [searchResults, setSearchResults] = React.useState<KnowledgeBaseArticle[]>([]);
    const [isSearching, setIsSearching] = React.useState<boolean>(false);
    const [isFocused, setIsFocused] = React.useState<boolean>(false);

    React.useEffect(() => {
        const searchPages = async () => {
            if (searchTerm.length >= 3) {
                setIsSearching(true);
                try {
                    const response = await knowledgeBaseService.searchPages(selectedLanguage, searchTerm);
                    setSearchResults(response.searchResults);
                } catch {
                    logger.error("Error searching pages");
                    setSearchResults([]);
                } finally {
                    setIsSearching(false);
                }
            } else {
                setSearchResults([]);
            }
        };

        const debounceTimer = setTimeout(() => {
            void searchPages();
        }, 300);

        return () => clearTimeout(debounceTimer);
    }, [searchTerm, selectedLanguage]);

    const clearSearch = () => {
        setSearchTerm("");
        setSearchResults([]);
    };

    const showClearButton = isFocused || searchTerm.length > 0;
    const showDropdown = searchTerm.length >= 3;

    return (
        <div className={styles.heroSection}>
            <div className={styles.heroContent}>
                <h1 className={styles.heroTitle}>{t("KnowledgeBase.searchHeader")}</h1>
                <div className={styles.searchWrapper}>
                    <span className={styles.searchIcon}>
                        <Search
                            color={isFocused ? style.primaryButtonBackgroundColor : style.inputHintColor}
                            height={"20"}
                            width={"20"}
                        />
                    </span>

                    <input
                        type="text"
                        placeholder={t("KnowledgeBase.searchPlaceholder")}
                        value={searchTerm}
                        maxLength={MAX_SEARCH_TERM_LENGTH}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        onFocus={() => setIsFocused(true)}
                        onBlur={() => setIsFocused(false)}
                        className={`${styles.heroSearchInput} ${showDropdown && searchResults.length > 0 ? styles.hasResults : ""}`}
                    />
                    {showClearButton && (
                        <button
                            className={styles.clearButton}
                            onMouseDown={(e) => e.preventDefault()}
                            onClick={clearSearch}
                            aria-label={t("Common.clear")}
                        >
                            <Cross
                                color={style.primaryButtonBackgroundActiveColor}
                                fill={"none"}
                                width={"24"}
                                height={"24"}
                            />
                        </button>
                    )}
                    {showDropdown && (
                        <div className={styles.searchDropdown}>
                            {isSearching ? (
                                <div className={styles.searchLoading}>{t("Common.search")}</div>
                            ) : searchResults.length > 0 ? (
                                searchResults.slice(0, 10).map((result) => (
                                    <div
                                        key={result.id}
                                        onClick={() => {
                                            onArticleClick(result);
                                            setSearchTerm("");
                                        }}
                                        className={styles.searchResult}
                                    >
                                        {result.title}
                                    </div>
                                ))
                            ) : (
                                <div className={styles.searchNoResults}>{t("KnowledgeBase.noResultsFound")}</div>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};
